import * as THREE from 'three';
import type { Vec2 } from '../state/types';
import { isInRiver, RIVER_Z } from './river';
import { createTerrainChunks } from './terrainChunks';

export interface EnvironmentHandles {
  bladesGroup: THREE.Group;
  updateVisualWorld: (target: THREE.Vector3) => void;
}

export function buildEnvironment(scene: THREE.Scene, baseStationPos: Vec2): EnvironmentHandles {
  const terrain = createTerrainChunks(scene);

  type TreeKind = 'pine' | 'round' | 'broad';

  const trunkMat = new THREE.MeshStandardMaterial({ color: 0x3d2715, roughness: 0.9 });
  const leafMats = {
    pine: new THREE.MeshStandardMaterial({ color: 0x13301a, roughness: 0.95, flatShading: true }),
    round: new THREE.MeshStandardMaterial({ color: 0x1a4a22, roughness: 0.95, flatShading: true }),
    broad: new THREE.MeshStandardMaterial({ color: 0x254d1c, roughness: 0.95, flatShading: true }),
  };

  function createTree(x: number, z: number, kind: TreeKind, scale = 1): void {
    if (isInRiver(x, z, 0.8 * scale)) return;

    const tree = new THREE.Group();
    const trunkH = (kind === 'pine' ? 1.05 : kind === 'broad' ? 0.85 : 0.95) * scale;
    const trunkGeo = new THREE.CylinderGeometry(
      0.1 * scale,
      (kind === 'broad' ? 0.22 : 0.18) * scale,
      trunkH,
      5,
    );
    const trunk = new THREE.Mesh(trunkGeo, trunkMat);
    trunk.position.y = trunkH * 0.5;
    trunk.castShadow = true;
    tree.add(trunk);

    const leafMat = leafMats[kind];

    if (kind === 'pine') {
      // Stacked cones — slightly richer than a single cone
      const tiers = [
        { y: trunkH + 0.15 * scale, r: 0.85 * scale, h: 1.1 * scale },
        { y: trunkH + 0.75 * scale, r: 0.62 * scale, h: 0.95 * scale },
        { y: trunkH + 1.25 * scale, r: 0.38 * scale, h: 0.75 * scale },
      ];
      for (const tier of tiers) {
        const cone = new THREE.Mesh(new THREE.ConeGeometry(tier.r, tier.h, 6), leafMat);
        cone.position.y = tier.y;
        cone.castShadow = true;
        tree.add(cone);
      }
    } else if (kind === 'round') {
      // Trunk + dual canopy blobs
      const lower = new THREE.Mesh(new THREE.IcosahedronGeometry(0.55 * scale, 0), leafMat);
      lower.position.set(0.05 * scale, trunkH + 0.2 * scale, -0.04 * scale);
      lower.scale.set(1.15, 0.9, 1.05);
      lower.castShadow = true;
      tree.add(lower);

      const upper = new THREE.Mesh(new THREE.IcosahedronGeometry(0.42 * scale, 0), leafMat);
      upper.position.set(-0.08 * scale, trunkH + 0.65 * scale, 0.06 * scale);
      upper.scale.set(1.05, 1.1, 0.95);
      upper.castShadow = true;
      tree.add(upper);
    } else {
      // Broad deciduous: short trunk, wide canopy + side puff
      const canopy = new THREE.Mesh(new THREE.IcosahedronGeometry(0.7 * scale, 0), leafMat);
      canopy.position.y = trunkH + 0.35 * scale;
      canopy.scale.set(1.35, 0.85, 1.2);
      canopy.castShadow = true;
      tree.add(canopy);

      const puff = new THREE.Mesh(new THREE.IcosahedronGeometry(0.38 * scale, 0), leafMat);
      puff.position.set(0.35 * scale, trunkH + 0.15 * scale, 0.1 * scale);
      puff.scale.set(1.1, 0.8, 1.0);
      puff.castShadow = true;
      tree.add(puff);
    }

    tree.position.set(x, 0, z);
    tree.rotation.y = (x * 1.7 + z * 0.9) % (Math.PI * 2);
    scene.add(tree);
  }

  // Edge / corner placements — clear of river, fields, roads, and base
  const trees: Array<[number, number, TreeKind, number]> = [
    [-14.5, -14.2, 'pine', 1.15],
    [-13.2, -12.4, 'round', 0.95],
    [-15.2, -10.5, 'broad', 0.85],
    [-12.8, -15.0, 'pine', 0.75],
    [-15.5, -7.8, 'round', 0.9],
    [14.2, -14.0, 'broad', 1.1],
    [15.0, -11.2, 'pine', 0.95],
    [13.4, -9.5, 'round', 0.8],
    [15.2, -7.2, 'pine', 0.85],
    [14.5, -4.5, 'broad', 0.9],
    [15.0, 8.5, 'round', 1.0],
    [13.8, 10.2, 'pine', 0.9],
    [15.2, 6.0, 'broad', 0.8],
    [-15.0, 7.5, 'pine', 1.05],
    [-13.5, 9.8, 'round', 0.85],
    [-15.2, 10.8, 'broad', 0.95],
    [-8.5, -14.5, 'pine', 0.8],
    [8.5, -14.8, 'round', 0.9],
    [10.5, -13.5, 'broad', 0.75],
    [-10.0, 11.5, 'pine', 0.85],
    [-14.8, -4.2, 'broad', 0.8],
    [-12.2, -13.8, 'round', 0.7],
    [12.0, -14.5, 'pine', 0.85],
    [15.3, -1.5, 'round', 0.95],
    [14.0, 4.2, 'pine', 0.75],
    [-6.5, 12.2, 'broad', 0.8],
    [3.5, -14.6, 'pine', 0.7],
    [-15.3, 2.0, 'round', 0.85],
  ];
  for (const [tx, tz, kind, sc] of trees) {
    createTree(tx, tz, kind, sc);
  }

  type RockKind = 'boulder' | 'flat' | 'cluster';

  const rockMats = {
    boulder: new THREE.MeshStandardMaterial({
      color: 0x3f3f46,
      roughness: 0.85,
      flatShading: true,
    }),
    flat: new THREE.MeshStandardMaterial({ color: 0x52525b, roughness: 0.9, flatShading: true }),
    cluster: new THREE.MeshStandardMaterial({
      color: 0x4a4a52,
      roughness: 0.88,
      flatShading: true,
    }),
  };

  function createRock(x: number, z: number, kind: RockKind, scale = 1): void {
    if (isInRiver(x, z, 0.5 * scale)) return;

    const group = new THREE.Group();
    const mat = rockMats[kind];

    if (kind === 'boulder') {
      const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(0.32 * scale, 0), mat);
      rock.position.y = 0.18 * scale;
      rock.scale.set(1.1, 0.85, 0.95);
      rock.rotation.set(0.3, 0.8, 0.1);
      rock.castShadow = true;
      group.add(rock);

      const chip = new THREE.Mesh(new THREE.TetrahedronGeometry(0.12 * scale, 0), mat);
      chip.position.set(0.22 * scale, 0.08 * scale, 0.1 * scale);
      chip.rotation.set(0.5, 1.2, 0.3);
      chip.castShadow = true;
      group.add(chip);
    } else if (kind === 'flat') {
      const slab = new THREE.Mesh(new THREE.DodecahedronGeometry(0.28 * scale, 0), mat);
      slab.position.y = 0.1 * scale;
      slab.scale.set(1.4, 0.45, 1.1);
      slab.rotation.y = 0.6;
      slab.castShadow = true;
      group.add(slab);
    } else {
      const parts = [
        { s: 0.28, x: 0, y: 0.14, z: 0 },
        { s: 0.18, x: 0.22, y: 0.1, z: 0.12 },
        { s: 0.15, x: -0.18, y: 0.08, z: 0.08 },
      ];
      for (const p of parts) {
        const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(p.s * scale, 0), mat);
        rock.position.set(p.x * scale, p.y * scale, p.z * scale);
        rock.rotation.set(p.x * 2, p.z * 3, 0.2);
        rock.castShadow = true;
        group.add(rock);
      }
    }

    group.position.set(x, 0, z);
    group.rotation.y = (x * 2.1 + z) % (Math.PI * 2);
    scene.add(group);
  }

  const rocks: Array<[number, number, RockKind, number]> = [
    [-8.2, -13.2, 'boulder', 1.4],
    [-7.4, -14.0, 'cluster', 1.0],
    [-9.0, -14.5, 'flat', 1.1],
    [12.2, 10.5, 'boulder', 1.3],
    [13.0, 9.2, 'flat', 0.95],
    [11.5, 11.2, 'cluster', 0.85],
    [-14.0, -6.5, 'flat', 1.2],
    [-13.2, 12.0, 'cluster', 1.0],
    [14.5, -13.5, 'boulder', 1.1],
    [8.0, -14.2, 'flat', 0.9],
    [-4.5, -14.5, 'cluster', 1.05],
    [15.0, 3.5, 'boulder', 0.85],
    [-15.0, 5.5, 'flat', 1.0],
    [6.5, 11.8, 'cluster', 0.9],
  ];
  for (const [rx, rz, kind, sc] of rocks) {
    createRock(rx, rz, kind, sc);
  }

  const bridgeGroup = new THREE.Group();
  bridgeGroup.position.set(0, 0, RIVER_Z);

  const abutmentGeo = new THREE.BoxGeometry(2.0, 0.4, 0.6);
  const abutmentMat = new THREE.MeshStandardMaterial({ color: 0x4b5563, roughness: 0.8 });

  const southAbutment = new THREE.Mesh(abutmentGeo, abutmentMat);
  southAbutment.position.set(0, 0.2, -1.8);
  southAbutment.receiveShadow = true;
  southAbutment.castShadow = true;
  bridgeGroup.add(southAbutment);

  const northAbutment = southAbutment.clone();
  northAbutment.position.z = 1.8;
  bridgeGroup.add(northAbutment);

  const girderGeo = new THREE.BoxGeometry(0.15, 0.2, 3.6);
  const girderMat = new THREE.MeshStandardMaterial({ color: 0x271c14, roughness: 0.9 });

  const girderLeft = new THREE.Mesh(girderGeo, girderMat);
  girderLeft.position.set(-0.75, 0.3, 0);
  girderLeft.castShadow = true;
  bridgeGroup.add(girderLeft);

  const girderRight = girderLeft.clone();
  girderRight.position.x = 0.75;
  bridgeGroup.add(girderRight);

  const plankColors = [0x543d2b, 0x4c3624, 0x5c4330, 0x453120];
  for (let i = 0; i < 13; i++) {
    const plankGeo = new THREE.BoxGeometry(1.8, 0.08, 0.24);
    const randomColor = plankColors[Math.floor(Math.random() * plankColors.length)]!;
    const plankMat = new THREE.MeshStandardMaterial({
      color: randomColor,
      roughness: 0.88,
      flatShading: true,
    });

    const plank = new THREE.Mesh(plankGeo, plankMat);
    const localZ = -1.65 + i * 0.275;
    plank.position.set(0, 0.41, localZ);

    plank.rotation.y = (Math.random() - 0.5) * 0.03;
    plank.rotation.x = (Math.random() - 0.5) * 0.02;
    plank.position.y += (Math.random() - 0.5) * 0.005;

    plank.castShadow = true;
    plank.receiveShadow = true;
    bridgeGroup.add(plank);
  }

  const postGeo = new THREE.BoxGeometry(0.08, 0.8, 0.08);
  const railBarGeo = new THREE.BoxGeometry(0.06, 0.06, 3.6);
  const railMat = new THREE.MeshStandardMaterial({ color: 0x3d2b1f, roughness: 0.9 });

  const postPositionsZ = [-1.6, -0.53, 0.53, 1.6];
  const sides = [-0.85, 0.85];

  sides.forEach((xSide) => {
    postPositionsZ.forEach((zPos) => {
      const post = new THREE.Mesh(postGeo, railMat);
      post.position.set(xSide, 0.75, zPos);
      post.castShadow = true;
      bridgeGroup.add(post);
    });

    const topRail = new THREE.Mesh(railBarGeo, railMat);
    topRail.position.set(xSide, 1.1, 0);
    topRail.castShadow = true;
    bridgeGroup.add(topRail);

    const midRail = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 3.6), railMat);
    midRail.position.set(xSide, 0.8, 0);
    midRail.castShadow = true;
    bridgeGroup.add(midRail);
  });

  scene.add(bridgeGroup);

  const baseGroup = new THREE.Group();
  baseGroup.position.set(baseStationPos.x, 0, baseStationPos.z);

  // Full base foundation (former parking-slab look, enlarged)
  const foundationMat = new THREE.MeshStandardMaterial({ color: 0x52525b, roughness: 0.9 });
  const foundation = new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.08, 10.2), foundationMat);
  foundation.position.y = 0.04;
  foundation.receiveShadow = true;
  baseGroup.add(foundation);

  // Dark metal for turbine pole (reused below)
  const padMat = new THREE.MeshStandardMaterial({ color: 0x27272a, roughness: 0.7 });

  // Agri-industrial depot: corrugated shed + tank + RTK antenna
  const shedGroup = new THREE.Group();
  const metalMat = new THREE.MeshStandardMaterial({
    color: 0x8a9199,
    roughness: 0.55,
    metalness: 0.35,
    flatShading: true,
  });
  const darkMetalMat = new THREE.MeshStandardMaterial({
    color: 0x3f3f46,
    roughness: 0.7,
    metalness: 0.25,
    flatShading: true,
  });
  const roofMat = new THREE.MeshStandardMaterial({
    color: 0x4b5563,
    roughness: 0.65,
    metalness: 0.3,
    flatShading: true,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: 0x3f6212,
    roughness: 0.75,
    flatShading: true,
  });
  const doorMat = new THREE.MeshStandardMaterial({
    color: 0x27272a,
    roughness: 0.8,
    flatShading: true,
  });
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0x64748b,
    roughness: 0.2,
    metalness: 0.2,
    flatShading: true,
  });
  const tankMat = new THREE.MeshStandardMaterial({
    color: 0xd6d3d1,
    roughness: 0.45,
    metalness: 0.4,
    flatShading: true,
  });
  const woodMat = new THREE.MeshStandardMaterial({
    color: 0x7c5a3a,
    roughness: 0.9,
    flatShading: true,
  });
  const hubMat = darkMetalMat;

  const shedW = 2.35; // along Z
  const shedD = 1.9; // along X
  const shedH = 1.45;
  const floorY = 0.08;

  const wall = new THREE.Mesh(new THREE.BoxGeometry(shedD, shedH, shedW), metalMat);
  wall.position.set(0, floorY + shedH * 0.5, 0);
  wall.castShadow = true;
  wall.receiveShadow = true;
  shedGroup.add(wall);

  // Corrugation ribs on long sides
  const ribGeo = new THREE.BoxGeometry(0.04, shedH * 0.92, 0.06);
  for (let i = 0; i < 5; i++) {
    const z = -shedW * 0.38 + i * (shedW * 0.19);
    for (const xSign of [-1, 1] as const) {
      const rib = new THREE.Mesh(ribGeo, darkMetalMat);
      rib.position.set(xSign * (shedD * 0.5 + 0.01), floorY + shedH * 0.5, z);
      shedGroup.add(rib);
    }
  }

  // Green band / trim
  const trim = new THREE.Mesh(new THREE.BoxGeometry(shedD + 0.04, 0.12, shedW + 0.04), accentMat);
  trim.position.set(0, floorY + 0.2, 0);
  shedGroup.add(trim);

  // Pitched roof (two slabs)
  const roofLen = shedD + 0.28;
  const roofSpan = shedW * 0.55;
  const roofThick = 0.08;
  const roofY = floorY + shedH + 0.12;
  for (const side of [-1, 1] as const) {
    const roof = new THREE.Mesh(new THREE.BoxGeometry(roofLen, roofThick, roofSpan), roofMat);
    roof.position.set(0, roofY, side * (roofSpan * 0.42));
    roof.rotation.x = side * 0.28;
    roof.castShadow = true;
    shedGroup.add(roof);
  }

  // Ridge cap
  const ridge = new THREE.Mesh(new THREE.BoxGeometry(roofLen + 0.05, 0.06, 0.14), darkMetalMat);
  ridge.position.set(0, floorY + shedH + 0.28, 0);
  shedGroup.add(ridge);

  // Bay door facing the road (+X)
  const bayFrame = new THREE.Mesh(new THREE.BoxGeometry(0.08, 1.15, 1.35), accentMat);
  bayFrame.position.set(shedD * 0.5 + 0.02, floorY + 0.62, 0);
  shedGroup.add(bayFrame);
  const bayDoor = new THREE.Mesh(new THREE.BoxGeometry(0.05, 1.0, 1.15), doorMat);
  bayDoor.position.set(shedD * 0.5 + 0.06, floorY + 0.58, 0);
  shedGroup.add(bayDoor);
  // Horizontal door slats
  for (let i = 0; i < 4; i++) {
    const slat = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.04, 1.1), darkMetalMat);
    slat.position.set(shedD * 0.5 + 0.09, floorY + 0.3 + i * 0.22, 0);
    shedGroup.add(slat);
  }

  // Small office window on south face
  const windowFrame = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.45, 0.06), darkMetalMat);
  windowFrame.position.set(0.15, floorY + 0.95, -shedW * 0.5 - 0.02);
  shedGroup.add(windowFrame);
  const windowGlass = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.32, 0.04), glassMat);
  windowGlass.position.set(0.15, floorY + 0.95, -shedW * 0.5 - 0.05);
  shedGroup.add(windowGlass);

  // Awning over bay
  const awning = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.05, 1.5), accentMat);
  awning.position.set(shedD * 0.5 + 0.28, floorY + 1.25, 0);
  awning.rotation.z = -0.15;
  awning.castShadow = true;
  shedGroup.add(awning);

  // Roof vents
  for (const z of [-0.45, 0.45]) {
    const vent = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 0.22, 6), darkMetalMat);
    vent.position.set(-0.25, floorY + shedH + 0.35, z);
    shedGroup.add(vent);
  }

  // Chemical / water tank (farm-industrial)
  const tank = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.48, 1.35, 10), tankMat);
  tank.position.set(-1.35, floorY + 0.68, 0.55);
  tank.castShadow = true;
  shedGroup.add(tank);
  const tankBand = new THREE.Mesh(new THREE.CylinderGeometry(0.49, 0.49, 0.1, 10), accentMat);
  tankBand.position.set(-1.35, floorY + 0.95, 0.55);
  shedGroup.add(tankBand);
  const tankCap = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.35, 0.18, 8), darkMetalMat);
  tankCap.position.set(-1.35, floorY + 1.42, 0.55);
  shedGroup.add(tankCap);
  const tankLegGeo = new THREE.BoxGeometry(0.1, 0.25, 0.1);
  for (const [dx, dz] of [
    [-0.28, -0.28],
    [0.28, -0.28],
    [-0.28, 0.28],
    [0.28, 0.28],
  ] as const) {
    const leg = new THREE.Mesh(tankLegGeo, darkMetalMat);
    leg.position.set(-1.35 + dx, floorY + 0.12, 0.55 + dz);
    shedGroup.add(leg);
  }

  // Pipe from tank to shed
  const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.7, 5), darkMetalMat);
  pipe.rotation.z = Math.PI / 2;
  pipe.position.set(-0.9, floorY + 0.45, 0.55);
  shedGroup.add(pipe);

  // Pallet stack (farm yard clutter)
  const pallet = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.12, 0.55), woodMat);
  pallet.position.set(-1.2, floorY + 0.14, -0.7);
  pallet.castShadow = true;
  shedGroup.add(pallet);
  const crate = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.4, 0.4), accentMat);
  crate.position.set(-1.2, floorY + 0.4, -0.7);
  crate.castShadow = true;
  shedGroup.add(crate);

  // Status light beside bay
  const statusLight = new THREE.Mesh(
    new THREE.BoxGeometry(0.1, 0.35, 0.1),
    new THREE.MeshStandardMaterial({
      color: 0x10b981,
      emissive: 0x10b981,
      emissiveIntensity: 0.55,
      flatShading: true,
    }),
  );
  statusLight.position.set(shedD * 0.5 + 0.08, floorY + 1.05, 0.75);
  shedGroup.add(statusLight);

  // RTK base antenna (pole + head + mid box)
  const rtkMat = new THREE.MeshStandardMaterial({
    color: 0x2a2a2e,
    roughness: 0.55,
    metalness: 0.35,
    flatShading: true,
  });
  const rtkSoftMat = new THREE.MeshStandardMaterial({
    color: 0x3f3f46,
    roughness: 0.65,
    flatShading: true,
  });
  const rtkGroup = new THREE.Group();
  rtkGroup.position.set(0.55, 0, 0.15);

  const mastH = 1.55;
  const mastBaseY = floorY + shedH;
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.04, mastH, 8), rtkMat);
  mast.position.y = mastBaseY + mastH * 0.5;
  mast.castShadow = true;
  rtkGroup.add(mast);

  // Flat rectangular GNSS / RTK head
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.1, 0.28), rtkSoftMat);
  head.position.y = mastBaseY + mastH + 0.05;
  head.castShadow = true;
  rtkGroup.add(head);
  const headCap = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.04, 0.22), rtkMat);
  headCap.position.y = mastBaseY + mastH + 0.12;
  rtkGroup.add(headCap);

  // Mid-pole interface box
  const midBox = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.22, 0.1), rtkSoftMat);
  midBox.position.set(0.08, mastBaseY + mastH * 0.45, 0);
  rtkGroup.add(midBox);
  const midLed = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.02, 0.02, 8),
    new THREE.MeshStandardMaterial({
      color: 0x10b981,
      emissive: 0x10b981,
      emissiveIntensity: 0.6,
      flatShading: true,
    }),
  );
  midLed.rotation.x = Math.PI / 2;
  midLed.position.set(0.135, mastBaseY + mastH * 0.48, 0);
  rtkGroup.add(midLed);

  shedGroup.add(rtkGroup);
  baseGroup.add(shedGroup);

  // Parking bay markings (south) — white dashed outline
  const parkingGroup = new THREE.Group();
  parkingGroup.position.set(0, 0, -3.2);

  const dashMat = new THREE.MeshStandardMaterial({
    color: 0xfafafa,
    roughness: 0.6,
    emissive: 0xffffff,
    emissiveIntensity: 0.15,
  });
  const dashY = 0.095;
  const bayW = 3.2;
  const bayD = 1.9;
  const halfW = bayW * 0.5;
  const halfD = bayD * 0.5;
  const dashLen = 0.4;
  const dashGap = 0.22;
  const dashThick = 0.08;

  function addDashedEdge(
    group: THREE.Group,
    axis: 'x' | 'z',
    length: number,
    fixed: number,
    alongSign: 1 | -1,
  ): void {
    const step = dashLen + dashGap;
    const count = Math.max(2, Math.floor((length + dashGap) / step));
    const span = (count - 1) * step;
    const start = -span * 0.5;
    for (let i = 0; i < count; i++) {
      const t = start + i * step;
      const geo =
        axis === 'x'
          ? new THREE.BoxGeometry(dashLen, 0.02, dashThick)
          : new THREE.BoxGeometry(dashThick, 0.02, dashLen);
      const dash = new THREE.Mesh(geo, dashMat);
      if (axis === 'x') {
        dash.position.set(t, dashY, fixed * alongSign);
      } else {
        dash.position.set(fixed * alongSign, dashY, t);
      }
      group.add(dash);
    }
  }

  addDashedEdge(parkingGroup, 'x', bayW, halfD, 1);
  addDashedEdge(parkingGroup, 'x', bayW, halfD, -1);
  addDashedEdge(parkingGroup, 'z', bayD, halfW, 1);
  addDashedEdge(parkingGroup, 'z', bayD, halfW, -1);
  baseGroup.add(parkingGroup);

  // Drone helipad (north) — orange disc + white H on the foundation
  const helipadGroup = new THREE.Group();
  helipadGroup.position.set(0, 0, 3.2);

  const helipadMat = new THREE.MeshStandardMaterial({
    color: 0xea580c,
    roughness: 0.65,
    emissive: 0xea580c,
    emissiveIntensity: 0.2,
  });
  const helipadDisc = new THREE.Mesh(new THREE.CylinderGeometry(1.15, 1.15, 0.06, 24), helipadMat);
  helipadDisc.position.y = 0.11;
  helipadDisc.receiveShadow = true;
  helipadGroup.add(helipadDisc);

  const hMarkMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.5,
    emissive: 0xffffff,
    emissiveIntensity: 0.2,
  });
  const hBarH = 0.7;
  const hBarW = 0.14;
  const hGap = 0.28;
  const hY = 0.155;
  const leftBar = new THREE.Mesh(new THREE.BoxGeometry(hBarW, 0.03, hBarH), hMarkMat);
  leftBar.position.set(-hGap, hY, 0);
  helipadGroup.add(leftBar);
  const rightBar = new THREE.Mesh(new THREE.BoxGeometry(hBarW, 0.03, hBarH), hMarkMat);
  rightBar.position.set(hGap, hY, 0);
  helipadGroup.add(rightBar);
  const crossBar = new THREE.Mesh(new THREE.BoxGeometry(hGap * 2 + hBarW, 0.03, hBarW), hMarkMat);
  crossBar.position.set(0, hY, 0);
  helipadGroup.add(crossBar);
  baseGroup.add(helipadGroup);

  scene.add(baseGroup);

  const turbineGroup = new THREE.Group();
  turbineGroup.position.set(-14, 0, 4);

  const poleGeo = new THREE.CylinderGeometry(0.1, 0.15, 4, 6);
  const pole = new THREE.Mesh(poleGeo, padMat);
  pole.position.y = 2;
  pole.castShadow = true;
  turbineGroup.add(pole);

  const turbineHubGeo = new THREE.SphereGeometry(0.18, 6, 6);
  const turbineHub = new THREE.Mesh(turbineHubGeo, hubMat);
  turbineHub.position.set(0, 4, 0.15);
  turbineGroup.add(turbineHub);

  const bladesGroup = new THREE.Group();
  bladesGroup.position.set(0, 4, 0.2);

  // Pivot at the hub end so bladesGroup.rotation spins around the turbine center
  const bladeGeo = new THREE.BoxGeometry(0.12, 1.4, 0.02);
  bladeGeo.translate(0, 0.7, 0);
  const bladeMat = new THREE.MeshStandardMaterial({ color: 0xf4f4f5 });

  for (let i = 0; i < 3; i++) {
    const blade = new THREE.Mesh(bladeGeo, bladeMat);
    blade.rotation.z = (i * Math.PI * 2) / 3;
    bladesGroup.add(blade);
  }
  turbineGroup.add(bladesGroup);
  scene.add(turbineGroup);

  const ROAD_W = 1.4;
  const ROAD_Y = 0.01;
  const colDirt = new THREE.Color(0x8a6b4a);
  const colDust = new THREE.Color(0x9a7a55);
  const colGrass = new THREE.Color(0x3d5c32);

  const dirtMat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.97,
    metalness: 0.0,
    flatShading: true,
  });

  function dirtColor(worldX: number, worldZ: number, edgeT: number): THREE.Color {
    const vColor = colDirt.clone().lerp(colDust, 0.3 + Math.sin(worldZ * 2 + worldX * 0.4) * 0.2);
    vColor.lerp(colGrass, edgeT * 0.75);
    vColor.multiplyScalar(1.0 + Math.sin(worldX * 8 + worldZ * 6) * 0.08);
    return vColor;
  }

  function createRoadSegment(
    length: number,
    width: number,
    x: number,
    z: number,
    rotationAngle = 0,
  ): void {
    if (length < 0.15) return;

    const roadGroup = new THREE.Group();
    roadGroup.position.set(x, ROAD_Y, z);
    roadGroup.rotation.y = rotationAngle;

    const segmentsL = Math.max(8, Math.floor(length * 2.5));
    const segmentsW = 6;
    const roadGeo = new THREE.PlaneGeometry(width, length, segmentsW, segmentsL);
    const roadPos = roadGeo.attributes.position;
    const colors: number[] = [];
    const halfW = width * 0.5;
    const halfLen = length * 0.5;
    const cosR = Math.cos(rotationAngle);
    const sinR = Math.sin(rotationAngle);

    for (let i = 0; i < roadPos.count; i++) {
      const vx = roadPos.getX(i);
      const vy = roadPos.getY(i);

      // Straight ends (no meander) so arms flush into the junction
      const endT = halfLen > 0.001 ? Math.abs(vy) / halfLen : 1;
      const endFade = endT > 0.55 ? Math.max(0, 1 - (endT - 0.55) / 0.45) : 1;
      const windX = Math.sin(vy * 0.35 + x * 0.2) * 0.1 * endFade;
      const localX = vx + windX;

      const edgeT = Math.min(1, Math.max(0, (Math.abs(localX) - halfW * 0.45) / (halfW * 0.55)));
      const heightOffset = 0.02 + Math.sin(vy * 1.8) * 0.003 * endFade + edgeT * 0.008;
      const noise = Math.sin(localX * 10 + vy * 4) * Math.cos(vy * 12) * 0.004 * endFade;

      roadPos.setX(i, localX);
      roadPos.setZ(i, heightOffset + noise);

      // World XZ after mesh rot.x=-PI/2 then group rot.y (see plane → -Z)
      const gx = localX * cosR + -vy * sinR;
      const gz = -localX * sinR + -vy * cosR;
      const vColor = dirtColor(x + gx, z + gz, edgeT);
      colors.push(vColor.r, vColor.g, vColor.b);
    }
    roadGeo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    roadGeo.computeVertexNormals();

    const roadMesh = new THREE.Mesh(roadGeo, dirtMat);
    roadMesh.rotation.x = -Math.PI / 2;
    roadMesh.receiveShadow = true;
    roadGroup.add(roadMesh);
    scene.add(roadGroup);
  }

  /** Fills the cross hole; same width as roads, grass only on diagonal corners. */
  function createJunction(x: number, z: number, size: number): void {
    const junctionGroup = new THREE.Group();
    junctionGroup.position.set(x, ROAD_Y, z);

    const segs = 6;
    const geo = new THREE.PlaneGeometry(size, size, segs, segs);
    const pos = geo.attributes.position;
    const colors: number[] = [];
    const half = size * 0.5;

    for (let i = 0; i < pos.count; i++) {
      const vx = pos.getX(i);
      const vy = pos.getY(i);

      // Only the four grass-facing corners soften — edges stay dirt where arms connect
      const nx = Math.abs(vx) / half;
      const ny = Math.abs(vy) / half;
      const cornerT = Math.min(nx, ny);
      const edgeT = Math.min(1, Math.max(0, (cornerT - 0.55) / 0.45));

      const heightOffset = 0.02 + edgeT * 0.006;
      const noise = Math.sin((x + vx) * 10 + (z - vy) * 4) * 0.003;
      pos.setZ(i, heightOffset + noise);

      // Same world-space palette as road arms (plane → world: X=vx, Z=-vy)
      const vColor = dirtColor(x + vx, z - vy, edgeT);
      colors.push(vColor.r, vColor.g, vColor.b);
    }
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geo.computeVertexNormals();

    const mesh = new THREE.Mesh(geo, dirtMat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.receiveShadow = true;
    junctionGroup.add(mesh);
    scene.add(junctionGroup);
  }

  // Exact road width — continuous plus, no oversized square tile
  const jSize = ROAD_W;
  const halfJ = jSize * 0.5;
  const worldEdge = 16; // ground is 32×32, centered at origin
  const roadWestEnd = baseStationPos.x; // stop at base center
  const southEnd = 14; // meet the bridge / river approach
  const spurHalf = 5;

  createJunction(0, 0, jSize);
  createJunction(-6, 0, jSize);

  // +Z toward the bridge; −Z to the world edge
  const toRiverLen = southEnd - halfJ;
  createRoadSegment(toRiverLen, ROAD_W, 0, halfJ + toRiverLen * 0.5, 0);
  const toEdgeZLen = worldEdge - halfJ;
  createRoadSegment(toEdgeZLen, ROAD_W, 0, -(halfJ + toEdgeZLen * 0.5), 0);

  // East to world edge; mid between junctions; west only as far as the base
  const eastLen = worldEdge - halfJ;
  createRoadSegment(eastLen, ROAD_W, halfJ + eastLen * 0.5, 0, Math.PI / 2);

  const midLen = 6 - jSize;
  createRoadSegment(midLen, ROAD_W, -3, 0, Math.PI / 2);

  const westLen = -6 - halfJ - roadWestEnd;
  createRoadSegment(westLen, ROAD_W, -6 - halfJ - westLen * 0.5, 0, Math.PI / 2);

  // Short spur at x = -6
  createRoadSegment(spurHalf - halfJ, ROAD_W, -6, halfJ + (spurHalf - halfJ) * 0.5, 0);
  createRoadSegment(spurHalf - halfJ, ROAD_W, -6, -(halfJ + (spurHalf - halfJ) * 0.5), 0);

  return { bladesGroup, updateVisualWorld: terrain.updateVisualWorld };
}
